import type {
  GroupHierarchy,
  GroupId,
  GroupCollapse,
} from './../../shared-types'

type HasCollapsedParentOptions = {
  groupCollapse: GroupCollapse
  groupHierarchy: GroupHierarchy
  groupId: GroupId
}

export const hasCollapsedParent = ({
  groupCollapse,
  groupHierarchy,
  groupId,
}: HasCollapsedParentOptions): boolean => {
  const currentHierarchy = groupHierarchy[groupId]

  if (currentHierarchy.level === 0) {
    return false
  }

  const currentParentId = currentHierarchy.parent

  return (
    groupCollapse[currentParentId] ||
    hasCollapsedParent({
      groupCollapse,
      groupHierarchy,
      groupId: currentParentId,
    })
  )
}

type WalkParentOptions = {
  hierarchy: GroupHierarchy
  groupId: GroupId
}

export const walkParents = ({
  hierarchy,
  groupId,
}: WalkParentOptions): GroupHierarchy => {
  // Top-level, just return
  if (hierarchy[groupId].level === 0) {
    return {}
  }

  const parentId = hierarchy[groupId].parent
  const parent = hierarchy[parentId]

  return {
    [parentId]: parent,
    ...walkParents({ hierarchy, groupId: parentId }),
  }
}

type MakeQueryHierarchyOptions = {
  includeParents?: boolean
  predicate: (_x: GroupId) => boolean
}

export const makeQueryHierarchy =
  ({ includeParents = true, predicate }: MakeQueryHierarchyOptions) =>
  (hierarchy: GroupHierarchy): GroupHierarchy =>
    Object.keys(hierarchy).reduce((acc, key) => {
      // Return early if already included
      if (acc[key]) {
        return acc
      }

      if (predicate(key)) {
        acc = {
          ...acc,
          // We ended up with the item that passed the predicate, plus all of its ancestors
          [key]: hierarchy[key],
          ...(includeParents
            ? walkParents({
                hierarchy,
                groupId: key,
              })
            : {}),
        }
      }

      return acc
    }, {} as GroupHierarchy)
